/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useLocation, useNavigate } from "react-router-dom";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ScheduleIcon from "@mui/icons-material/Schedule";
import PendingIcon from "@mui/icons-material/Pending";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAvatar from "components/MDAvatar";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import { API_ESA_OFFICE, API_ESA_OFFICE_UPLOAD } from "utils";
import { logoutUser } from "actions/AuthAction";

function KinerjaDisposisiDetail() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { penerimaId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [detailList, setDetailList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState(null);
  const [tahun, setTahun] = useState(null);
  const [bulan, setBulan] = useState(null);
  const [penerima, setPenerima] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [openFileDialog, setOpenFileDialog] = useState(false);
  const [originalFileUrl, setOriginalFileUrl] = useState(null);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user) {
      setUserId(user.uid);
    } else {
      logoutUser(navigate);
    }

    // Ambil data dari state navigation
    if (location.state) {
      setPenerima(location.state.penerima);
      setTahun(location.state.tahun);
      setBulan(location.state.bulan);
    } else {
      // Jika tidak ada state, ambil dari URL atau set default
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth() + 1;
      setTahun(currentYear);
      setBulan(currentMonth);
    }
  }, [location, navigate]);

  useEffect(() => {
    if (userId && penerimaId && tahun) {
      fetchDetail();
    }
  }, [userId, penerimaId, tahun, bulan]);

  const fetchDetail = async () => {
    try {
      const bulanParam =
        bulan && bulan !== "semua" && bulan !== "0" ? `&bulan=${bulan}` : "";
      const response = await axios.get(
        `${API_ESA_OFFICE}disposisi/kinerja/${userId}/detail/${penerimaId}?tahun=${tahun}${bulanParam}`
      );
      setDetailList(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching detail:", error);
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("id-ID", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const formatProfilUrl = (profil, uid_RSS) => {
    if (uid_RSS) {
      return `https://smartdh.rotendaokab.go.id/uploads/${uid_RSS}.jpg`;
    }
    if (profil) {
      return `${API_ESA_OFFICE_UPLOAD}${profil}`;
    }
    return null;
  };

  // Fungsi untuk mendapatkan icon berdasarkan jenis file
  const getFileIcon = (filename) => {
    if (!filename) return <Icon>insert_drive_file</Icon>;

    const extension = filename.split(".").pop()?.toLowerCase();

    switch (extension) {
      case "pdf":
        return <Icon sx={{ color: "#f44336" }}>picture_as_pdf</Icon>;
      case "doc":
      case "docx":
        return <Icon sx={{ color: "#2196f3" }}>description</Icon>;
      case "xls":
      case "xlsx":
        return <Icon sx={{ color: "#4caf50" }}>table_chart</Icon>;
      case "jpg":
      case "jpeg":
      case "png":
        return <Icon sx={{ color: "#9c27b0" }}>image</Icon>;
      default:
        return <Icon>insert_drive_file</Icon>;
    }
  };

  const handleOpenPdfDialog = (fileUrl) => {
    setOriginalFileUrl(fileUrl);
    setSelectedFile(fileUrl);
    setOpenFileDialog(true);
  };

  const handleCloseFileDialog = () => {
    setOpenFileDialog(false);
    setSelectedFile(null);
    setOriginalFileUrl(null);
  };

  // Fungsi helper untuk render status
  const renderStatus = (item) => {
    const isCompleted =
      item.waktu_selesai &&
      item.waktu_selesai !== null &&
      item.waktu_selesai !== "";
    const isRead = item.waktu_baca && item.waktu_baca !== null;

    return (
      <Chip
        icon={
          isCompleted ? (
            <CheckCircleIcon />
          ) : isRead ? (
            <ScheduleIcon />
          ) : (
            <PendingIcon />
          )
        }
        label={isCompleted ? "Selesai" : isRead ? "Dibaca" : "Pending"}
        color={isCompleted ? "success" : isRead ? "info" : "warning"}
        size="small"
      />
    );
  };

  // Fungsi helper untuk render file list
  const renderFileList = (files) => {
    if (!files || files.length === 0) return "-";
    return (
      <MDBox display="flex" flexDirection="column" gap={0.5}>
        {files.map((file, idx) => {
          const filename = file.split("/").pop();
          return (
            <Button
              key={idx}
              href={`${API_ESA_OFFICE_UPLOAD}${file}`}
              target="_blank"
              variant="outlined"
              size="small"
              startIcon={getFileIcon(filename)}
              sx={{ justifyContent: "flex-start", textTransform: "none" }}
            >
              {filename}
            </Button>
          );
        })}
      </MDBox>
    );
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDBox
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  width="100%"
                >
                  <MDTypography variant="h6" color="white">
                    Detail Kinerja Disposisi
                    {penerima && ` - ${penerima.nama_penerima}`}
                  </MDTypography>
                  <Button
                    onClick={() => navigate(-1)}
                    variant="contained"
                    sx={{
                      color: "#000000",
                      backgroundColor: "#ffffff",
                      "&:hover": {
                        backgroundColor: "#f5f5f5",
                      },
                    }}
                  >
                    Kembali
                  </Button>
                </MDBox>
              </MDBox>
              <MDBox pt={4} pb={3} px={3}>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" py={4}>
                    <MDTypography variant="body2">Memuat data...</MDTypography>
                  </MDBox>
                ) : detailList.length === 0 ? (
                  <MDBox
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    py={4}
                  >
                    <MDTypography variant="h6" color="text.secondary">
                      Tidak ada data disposisi
                    </MDTypography>
                  </MDBox>
                ) : isMobile ? (
                  <MDBox display="flex" flexDirection="column" gap={2} px={2}>
                    {detailList.map((item, index) => {
                      const isCompleted =
                        item.waktu_selesai &&
                        item.waktu_selesai !== null &&
                        item.waktu_selesai !== "";
                      const isRead =
                        item.waktu_baca && item.waktu_baca !== null;

                      return (
                        <Card key={item.disposisi_penerima_id || index}>
                          <CardContent>
                            {/* No. Surat */}
                            <MDBox mb={1}>
                              <MDTypography
                                variant="caption"
                                color="text.secondary"
                                fontWeight="medium"
                              >
                                No. Surat:
                              </MDTypography>
                              <MDTypography variant="body2" color="text">
                                {item.nomor_surat || "-"}
                              </MDTypography>
                            </MDBox>

                            {/* File Surat */}
                            {item.file_surat && (
                              <MDBox mb={1}>
                                <MDTypography
                                  variant="caption"
                                  color="text.secondary"
                                  fontWeight="medium"
                                  mb={0.5}
                                >
                                  File Surat:
                                </MDTypography>
                                <Button
                                  variant="contained"
                                  size="small"
                                  startIcon={<PictureAsPdfIcon />}
                                  onClick={() =>
                                    handleOpenPdfDialog(
                                      `${API_ESA_OFFICE_UPLOAD}/uploads/${item.file_surat}`
                                    )
                                  }
                                >
                                  Lihat File Surat
                                </Button>
                              </MDBox>
                            )}

                            {/* Tanggal Disposisi */}
                            <MDBox mb={1}>
                              <MDTypography
                                variant="caption"
                                color="text.secondary"
                                fontWeight="medium"
                              >
                                Tanggal Disposisi:
                              </MDTypography>
                              <MDTypography variant="body2" color="text">
                                {formatDate(item.tanggal_disposisi)}
                              </MDTypography>
                            </MDBox>

                            {/* Catatan Disposisi */}
                            <MDBox mb={1}>
                              <MDTypography
                                variant="caption"
                                color="text.secondary"
                                fontWeight="medium"
                              >
                                Catatan Disposisi:
                              </MDTypography>
                              <MDTypography variant="body2" color="text">
                                {item.catatan_disposisi || "-"}
                              </MDTypography>
                            </MDBox>

                            {/* Tingkat Perintah */}
                            <MDBox mb={1}>
                              <MDTypography
                                variant="caption"
                                color="text.secondary"
                                fontWeight="medium"
                                mb={0.5}
                              >
                                Tingkat Perintah:
                              </MDTypography>
                              <Chip
                                label={
                                  item.tingkat_perintah === "1"
                                    ? "Segera"
                                    : item.tingkat_perintah === "2"
                                    ? "Biasa"
                                    : item.tingkat_perintah === "3"
                                    ? "Penting"
                                    : item.tingkat_perintah
                                }
                                color={
                                  item.tingkat_perintah === "1"
                                    ? "error"
                                    : item.tingkat_perintah === "2"
                                    ? "info"
                                    : item.tingkat_perintah === "3"
                                    ? "warning"
                                    : "default"
                                }
                                size="small"
                              />
                            </MDBox>

                            {/* Status */}
                            <MDBox mb={1}>
                              <MDTypography
                                variant="caption"
                                color="text.secondary"
                                fontWeight="medium"
                                mb={0.5}
                              >
                                Status:
                              </MDTypography>
                              {renderStatus(item)}
                            </MDBox>

                            {/* Waktu Dibaca */}
                            {item.waktu_baca && (
                              <MDBox mb={1}>
                                <MDTypography
                                  variant="caption"
                                  color="text.secondary"
                                  fontWeight="medium"
                                >
                                  Waktu Dibaca:
                                </MDTypography>
                                <MDTypography variant="body2" color="text">
                                  {formatDate(item.waktu_baca)}
                                </MDTypography>
                              </MDBox>
                            )}

                            {/* Waktu Selesai */}
                            {item.waktu_selesai && (
                              <MDBox mb={1}>
                                <MDTypography
                                  variant="caption"
                                  color="text.secondary"
                                  fontWeight="medium"
                                >
                                  Waktu Selesai:
                                </MDTypography>
                                <MDTypography variant="body2" color="text">
                                  {formatDate(item.waktu_selesai)}
                                </MDTypography>
                              </MDBox>
                            )}

                            {/* Respon Pemberi Disposisi */}
                            {item.respon_pemberi_disposisi && (
                              <MDBox mb={1}>
                                <MDTypography
                                  variant="caption"
                                  color="text.secondary"
                                  fontWeight="medium"
                                  mb={0.5}
                                >
                                  Respon:
                                </MDTypography>
                                <Chip
                                  label={item.respon_pemberi_disposisi}
                                  color={
                                    item.respon_pemberi_disposisi === "Terima"
                                      ? "success"
                                      : item.respon_pemberi_disposisi ===
                                        "Tolak"
                                      ? "error"
                                      : "warning"
                                  }
                                  size="small"
                                  sx={{ mr: 1 }}
                                />
                                {item.respon_pemberi_disposisi === "Terima" &&
                                  item.nilai && (
                                    <MDBox
                                      display="flex"
                                      alignItems="center"
                                      gap={0.5}
                                      mt={0.5}
                                    >
                                      <MDTypography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        Rating:
                                      </MDTypography>
                                      {[1, 2, 3, 4, 5].map((star) => (
                                        <Icon
                                          key={star}
                                          sx={{
                                            color:
                                              star <= item.nilai
                                                ? "#FFD700"
                                                : "#E0E0E0",
                                            fontSize: "1.2rem",
                                          }}
                                        >
                                          star
                                        </Icon>
                                      ))}
                                      <MDTypography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        ({item.nilai}/5)
                                      </MDTypography>
                                    </MDBox>
                                  )}
                              </MDBox>
                            )}

                            {/* File Disposisi */}
                            {item.file_disposisi &&
                              item.file_disposisi.length > 0 && (
                                <MDBox mb={2}>
                                  <MDTypography
                                    variant="caption"
                                    color="text.secondary"
                                    fontWeight="medium"
                                    mb={0.5}
                                  >
                                    File Disposisi:
                                  </MDTypography>
                                  {renderFileList(item.file_disposisi)}
                                </MDBox>
                              )}

                            {/* File Tindak Lanjut */}
                            {item.file_TL_disposisi &&
                              item.file_TL_disposisi.length > 0 && (
                                <MDBox mb={2}>
                                  <MDTypography
                                    variant="caption"
                                    color="text.secondary"
                                    fontWeight="medium"
                                    mb={0.5}
                                  >
                                    File Tindak Lanjut:
                                  </MDTypography>
                                  {renderFileList(item.file_TL_disposisi)}
                                </MDBox>
                              )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </MDBox>
                ) : (
                  <DataTable
                    table={{
                      columns: [
                        {
                          Header: "No",
                          accessor: "no",
                          width: "5%",
                          Cell: ({ row }) => (
                            <MDTypography variant="caption" color="text">
                              {row.index + 1}
                            </MDTypography>
                          ),
                        },
                        {
                          Header: "No. Surat",
                          accessor: "nomor_surat",
                          width: "10%",
                        },
                        {
                          Header: "File Surat",
                          accessor: "file_surat",
                          width: "8%",
                          Cell: ({ value, row }) =>
                            value ? (
                              <Button
                                variant="contained"
                                size="small"
                                startIcon={<PictureAsPdfIcon />}
                                onClick={() =>
                                  handleOpenPdfDialog(
                                    `${API_ESA_OFFICE_UPLOAD}/uploads/${value}`
                                  )
                                }
                              >
                                Lihat
                              </Button>
                            ) : (
                              "-"
                            ),
                        },
                        {
                          Header: "Tanggal Disposisi",
                          accessor: "tanggal_disposisi",
                          width: "10%",
                          Cell: ({ value }) => formatDate(value),
                        },
                        {
                          Header: "Catatan Disposisi",
                          accessor: "catatan_disposisi",
                          width: "15%",
                          Cell: ({ value }) => (
                            <MDBox
                              sx={{
                                whiteSpace: "normal",
                                wordWrap: "break-word",
                                maxWidth: "200px",
                                py: 1,
                              }}
                            >
                              <MDTypography variant="caption" color="text">
                                {value || "-"}
                              </MDTypography>
                            </MDBox>
                          ),
                        },
                        {
                          Header: "Tingkat Perintah",
                          accessor: "tingkat_perintah",
                          width: "8%",
                          Cell: ({ value }) => {
                            const label =
                              value === "1"
                                ? "Segera"
                                : value === "2"
                                ? "Biasa"
                                : value === "3"
                                ? "Penting"
                                : value;
                            const color =
                              value === "1"
                                ? "error"
                                : value === "2"
                                ? "info"
                                : value === "3"
                                ? "warning"
                                : "default";
                            return (
                              <Chip label={label} color={color} size="small" />
                            );
                          },
                        },
                        {
                          Header: "Status",
                          accessor: "status",
                          width: "8%",
                          Cell: ({ row }) => renderStatus(row.original),
                        },
                        {
                          Header: "Waktu Dibaca",
                          accessor: "waktu_baca",
                          width: "10%",
                          Cell: ({ value }) =>
                            value ? formatDate(value) : "-",
                        },
                        {
                          Header: "Waktu Selesai",
                          accessor: "waktu_selesai",
                          width: "10%",
                          Cell: ({ value }) =>
                            value ? formatDate(value) : "-",
                        },
                        {
                          Header: "Respon",
                          accessor: "respon_pemberi_disposisi",
                          width: "10%",
                          Cell: ({ row }) => {
                            const respon =
                              row.original.respon_pemberi_disposisi;
                            const nilai = row.original.nilai;
                            if (!respon) {
                              return (
                                <MDTypography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  -
                                </MDTypography>
                              );
                            }
                            return (
                              <MDBox
                                display="flex"
                                flexDirection="column"
                                gap={0.5}
                              >
                                <Chip
                                  label={respon}
                                  color={
                                    respon === "Terima"
                                      ? "success"
                                      : respon === "Tolak"
                                      ? "error"
                                      : "warning"
                                  }
                                  size="small"
                                />
                                {respon === "Terima" && nilai && (
                                  <MDBox
                                    display="flex"
                                    alignItems="center"
                                    gap={0.5}
                                  >
                                    {[1, 2, 3, 4, 5].map((star) => (
                                      <Icon
                                        key={star}
                                        sx={{
                                          color:
                                            star <= nilai
                                              ? "#FFD700"
                                              : "#E0E0E0",
                                          fontSize: "1rem",
                                        }}
                                      >
                                        star
                                      </Icon>
                                    ))}
                                    <MDTypography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      ({nilai}/5)
                                    </MDTypography>
                                  </MDBox>
                                )}
                              </MDBox>
                            );
                          },
                        },
                        {
                          Header: "File Disposisi",
                          accessor: "file_disposisi",
                          width: "12%",
                          Cell: ({ value }) => renderFileList(value),
                        },
                        {
                          Header: "File TL",
                          accessor: "file_TL_disposisi",
                          width: "12%",
                          Cell: ({ value }) => renderFileList(value),
                        },
                      ],
                      rows: detailList,
                    }}
                    entriesPerPage={{
                      defaultValue: 10,
                      entries: [5, 10, 15, 20, 25],
                    }}
                    showTotalEntries={true}
                    isSorted={true}
                    noEndBorder
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Dialog untuk menampilkan file PDF */}
      <Dialog
        open={openFileDialog}
        onClose={handleCloseFileDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <MDBox display="flex" alignItems="center">
              <PictureAsPdfIcon sx={{ mr: 1, color: "error.main" }} />
              <MDTypography variant="h6">File Surat</MDTypography>
            </MDBox>
            <IconButton onClick={handleCloseFileDialog} size="small">
              <CloseIcon />
            </IconButton>
          </MDBox>
          <MDBox mt={2} display="flex" justifyContent="flex-start" gap={2}>
            <Button
              variant="outlined"
              onClick={() => {
                if (originalFileUrl) {
                  window.open(originalFileUrl, "_blank", "noopener,noreferrer");
                }
              }}
              sx={{
                borderColor: "#000000",
                color: "#000000",
                "&:hover": {
                  borderColor: "#000000",
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              Buka di Tab Baru
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                if (originalFileUrl) {
                  const link = document.createElement("a");
                  link.href = originalFileUrl;
                  link.download = originalFileUrl.split("/").pop();
                  link.click();
                }
              }}
              sx={{
                borderColor: "#000000",
                color: "#000000",
                "&:hover": {
                  borderColor: "#000000",
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              Download
            </Button>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox pt={2} pb={3} px={3}>
            {selectedFile ? (
              <iframe
                src={selectedFile}
                width="100%"
                height="600px"
                title="PDF Viewer"
                style={{ border: "none" }}
                onError={(e) => {
                  console.error("Error loading PDF:", e);
                }}
              />
            ) : (
              <MDBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="600px"
              >
                <MDTypography variant="h6" color="text">
                  File tidak tersedia
                </MDTypography>
              </MDBox>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseFileDialog}
            variant="contained"
            color="error"
            startIcon={<CloseIcon />}
          >
            Tutup
          </Button>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default KinerjaDisposisiDetail;
